import { useState, type ReactNode } from 'react';
import { Page, PageHeader } from '../components/Page';
import { StatCard } from '../components/StatCard';
import { PCard } from '../components/PCard';
import { InvoiceTable } from '../components/InvoiceTable';
import { Async } from '../components/Async';
import { IcPill, IcCalendar } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import { getSummary, getInvoices, runPharmacyBilling, ApiError } from '../lib/api';
import { fmtCents0 } from '../lib/format';

export const PharmacyFeesPage = ({ tabs }: { tabs?: ReactNode } = {}) => {
  const { can } = useAuth();
  const summary = useFetch(getSummary);
  const invoices = useFetch(() => getInvoices({ flow: 'pharmacy' }));
  const pharmacyTotal = summary.data?.byFlow.find((b) => b.flow === 'pharmacy')?.totalCents ?? 0;
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);

  async function run(draft = false) {
    setRunning(true);
    setRunMsg(null);
    try {
      const r = await runPharmacyBilling({ draft });
      const verb = draft ? 'Drafted' : 'Invoiced';
      const tail = draft ? ' — review, then finalize to send.' : '.';
      setRunMsg(
        `${verb} ${r.ordersInvoiced} orders + ${r.shipmentsInvoiced} shipments across ${r.invoiceCount} invoices (${fmtCents0(r.grossCents)})${tail}`,
      );
      invoices.reload();
      summary.reload();
    } catch (err) {
      setRunMsg(err instanceof ApiError ? err.message : 'Run failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <Page>
      <PageHeader
        tabs={tabs}
        title="Pharmacy Fees"
        sub="Pharmacy usage × pricing table + shipping · one combined invoice · Hyperswitch (pharmacy is biller)"
        actions={
          can('billing:run') ? (
            <>
              <button className="btn btn--ghost" onClick={() => run(true)} disabled={running}>
                {running ? 'Running…' : 'Generate drafts'}
              </button>
              <button className="btn btn--primary" onClick={() => run(false)} disabled={running}>
                {running ? 'Running…' : 'Run pharmacy billing'}
              </button>
            </>
          ) : undefined
        }
      />

      {runMsg && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(16,185,129,.08)', color: 'var(--status-success-ink,#047857)', fontSize: 13 }}>
          {runMsg}
        </div>
      )}

      <div className="grid grid-3">
        <StatCard tone="success" icon={IcPill} label="Pharmacy invoiced" value={fmtCents0(pharmacyTotal)} />
        <StatCard tone="staged" icon={IcCalendar} label="Next run" value="Sunday" sub="via Hyperswitch" />
        <div />
      </div>

      <PCard style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div className="p-stat__icon p-stat__icon--in-progress">
          <IcPill size={18} />
        </div>
        <div style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>
          Fees are computed from pharmacy usage (read from the pharmacy DB) priced against the pricing
          table this app owns — update rates under <strong style={{ color: 'var(--p-foreground)' }}>Pricing</strong>.
          Each client's FedEx shipments ride on the same invoice (rates under{' '}
          <strong style={{ color: 'var(--p-foreground)' }}>Shipping Fees</strong>). Charges are orchestrated
          through Hyperswitch on the pharmacy's behalf.
        </div>
      </PCard>

      <div>
        <div className="section-title">Pharmacy invoices</div>
        <Async state={invoices} isEmpty={(d) => d.invoices.length === 0} emptyLabel="No pharmacy invoices yet.">
          {({ invoices: rows }) => <InvoiceTable invoices={rows} showType={false} />}
        </Async>
      </div>
    </Page>
  );
};
